import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiService } from "../../../services/api";

const PAGE_SIZE = 50;

interface LoginAuditRow {
    id: string;
    loginname: string;
    ip: string;
    info: string;
    created_at: string;
}

interface ActivityAuditRow {
    id: string;
    modulename: string;
    username: string;
    data: string;
    time: string;
    type: string;
    created_at: string;
    module_id?: string | null;
}

interface PaginatedAuditResponse<T> {
    success: boolean;
    totalcount?: number;
    data: T[];
}

function formatTimestamp(iso: string): string {
    if (!iso) return "—";
    try {
        const d = new Date(iso.replace(" ", "T"));
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

/** Parse API date strings (ISO, space-separated, etc.) */
function parseAuditTimestamp(raw: string | undefined): Date | null {
    if (!raw?.trim()) return null;
    const t = raw.trim();
    let d = new Date(t.includes("T") ? t : t.replace(" ", "T"));
    if (!Number.isNaN(d.getTime())) return d;
    d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
}

function parseYmdLocalStart(ymd: string): Date | null {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
    const [y, m, day] = ymd.split("-").map(Number);
    const d = new Date(y, m - 1, day, 0, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
}

function parseYmdLocalEnd(ymd: string): Date | null {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
    const [y, m, day] = ymd.split("-").map(Number);
    const d = new Date(y, m - 1, day, 23, 59, 59, 999);
    return Number.isNaN(d.getTime()) ? null : d;
}

function activityRowInstant(row: ActivityAuditRow): Date | null {
    return parseAuditTimestamp(row.time) ?? parseAuditTimestamp(row.created_at);
}

/** Front-end date range: inclusive local calendar days */
function rowMatchesDateRange(
    ts: Date | null,
    dateFrom: string,
    dateTo: string,
    enabled: boolean
): boolean {
    if (!enabled) return true;
    if (!ts) return false;
    if (dateFrom) {
        const start = parseYmdLocalStart(dateFrom);
        if (start && ts < start) return false;
    }
    if (dateTo) {
        const end = parseYmdLocalEnd(dateTo);
        if (end && ts > end) return false;
    }
    return true;
}

function truncate(str: string, max: number): string {
    if (!str || str.length <= max) return str || "—";
    return str.slice(0, max) + "…";
}

function typeBadgeClass(type: string): string {
    const t = (type || "").toLowerCase();
    if (t === "add" || t === "insert")
        return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300";
    if (t === "edit" || t === "update")
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300";
    if (t === "delete" || t === "remove")
        return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
    return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";
}

function formatAuditValue(v: unknown): string {
    if (v === null || v === undefined) return "—";
    if (typeof v === "string") return v === "" ? "—" : v;
    try {
        return JSON.stringify(v);
    } catch {
        return String(v);
    }
}

function maskSensitiveField(name: string, value: string): string {
    if (!value || value === "—") return value;
    if (/password|passwd|pwd/i.test(name)) return "••••••••";
    return value;
}

interface ParsedFieldRow {
    displayLabel: string;
    fieldName: string;
    oldVal: string;
    newVal: string;
}

/** Pull human-readable field rows from API `data` JSON string */
function parseAuditDataPayload(dataStr: string): {
    fields: ParsedFieldRow[];
    submoduleBlocks: string[];
    parseError: boolean;
} {
    const submoduleBlocks: string[] = [];
    const fields: ParsedFieldRow[] = [];
    if (!dataStr?.trim()) return { fields, submoduleBlocks, parseError: false };

    let parsed: unknown;
    try {
        parsed = JSON.parse(dataStr);
    } catch {
        return { fields, submoduleBlocks, parseError: true };
    }

    const visitArray = (arr: unknown[]) => {
        for (const item of arr) {
            if (!item || typeof item !== "object" || Array.isArray(item)) continue;
            const rec = item as Record<string, unknown>;
            if ("submodule" in rec) {
                try {
                    submoduleBlocks.push(JSON.stringify(rec.submodule, null, 2));
                } catch {
                    submoduleBlocks.push(String(rec.submodule));
                }
                continue;
            }
            const name = String(rec.name ?? "");
            const label = String(rec.label ?? "").trim();
            const displayLabel = label || name || "Field";
            if (name || label || "oldvalue" in rec || "newvalue" in rec) {
                const oldVal = maskSensitiveField(name, formatAuditValue(rec.oldvalue));
                const newVal = maskSensitiveField(name, formatAuditValue(rec.newvalue));
                fields.push({
                    displayLabel,
                    fieldName: name,
                    oldVal,
                    newVal,
                });
            }
        }
    };

    if (Array.isArray(parsed)) {
        visitArray(parsed);
    } else {
        try {
            submoduleBlocks.push(JSON.stringify(parsed, null, 2));
        } catch {
            submoduleBlocks.push(String(parsed));
        }
    }

    return { fields, submoduleBlocks, parseError: false };
}

export const AuditLogsPanel: React.FC = () => {
    const [subTab, setSubTab] = useState<"activity" | "login">("activity");
    const [skip, setSkip] = useState(0);
    const [detailRow, setDetailRow] = useState<ActivityAuditRow | null>(null);

    const [loginRows, setLoginRows] = useState<LoginAuditRow[]>([]);
    const [loginTotal, setLoginTotal] = useState(0);
    const [loginLoading, setLoginLoading] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    const [activityRows, setActivityRows] = useState<ActivityAuditRow[]>([]);
    const [activityTotal, setActivityTotal] = useState(0);
    const [activityLoading, setActivityLoading] = useState(false);
    const [activityError, setActivityError] = useState<string | null>(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const fetchLogin = useCallback(async () => {
        setLoginLoading(true);
        setLoginError(null);
        try {
            const res = await apiService.get<PaginatedAuditResponse<LoginAuditRow>>(
                "/pabws/audit/login",
                { skip, limit: PAGE_SIZE },
                { includeAuthToken: true, authHeaderType: "Bearer" }
            );
            if (res?.success === false) {
                setLoginError("Could not load login history.");
                setLoginRows([]);
                setLoginTotal(0);
                return;
            }
            setLoginRows(Array.isArray(res?.data) ? res.data : []);
            setLoginTotal(typeof res?.totalcount === "number" ? res.totalcount : 0);
        } catch (e: any) {
            setLoginError(e?.message || "Failed to load login history.");
            setLoginRows([]);
            setLoginTotal(0);
        } finally {
            setLoginLoading(false);
        }
    }, [skip]);

    const fetchActivity = useCallback(async () => {
        setActivityLoading(true);
        setActivityError(null);
        try {
            const res = await apiService.get<PaginatedAuditResponse<ActivityAuditRow>>(
                "/pabws/audit/log",
                { skip, limit: PAGE_SIZE },
                { includeAuthToken: true, authHeaderType: "Bearer" }
            );
            if (res?.success === false) {
                setActivityError("Could not load activity log.");
                setActivityRows([]);
                setActivityTotal(0);
                return;
            }
            setActivityRows(Array.isArray(res?.data) ? res.data : []);
            setActivityTotal(typeof res?.totalcount === "number" ? res.totalcount : 0);
        } catch (e: any) {
            setActivityError(e?.message || "Failed to load activity log.");
            setActivityRows([]);
            setActivityTotal(0);
        } finally {
            setActivityLoading(false);
        }
    }, [skip]);

    useEffect(() => {
        setSkip(0);
        setDetailRow(null);
        setSearchQuery("");
        setDateFrom("");
        setDateTo("");
    }, [subTab]);

    const searchNorm = searchQuery.trim().toLowerCase();

    const dateRangeInvalid = useMemo(() => {
        if (!dateFrom || !dateTo) return false;
        const s = parseYmdLocalStart(dateFrom);
        const e = parseYmdLocalEnd(dateTo);
        if (!s || !e) return false;
        return s.getTime() > e.getTime();
    }, [dateFrom, dateTo]);

    const applyDateFilter = !dateRangeInvalid && (!!dateFrom || !!dateTo);

    const filteredLoginRows = useMemo(() => {
        return loginRows.filter((row) => {
            const ts = parseAuditTimestamp(row.created_at);
            if (!rowMatchesDateRange(ts, dateFrom, dateTo, applyDateFilter)) return false;
            if (!searchNorm) return true;
            const blob = [row.loginname, row.ip, row.info, row.created_at]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return blob.includes(searchNorm);
        });
    }, [loginRows, searchNorm, dateFrom, dateTo, applyDateFilter]);

    const filteredActivityRows = useMemo(() => {
        return activityRows.filter((row) => {
            const ts = activityRowInstant(row);
            if (!rowMatchesDateRange(ts, dateFrom, dateTo, applyDateFilter)) return false;
            if (!searchNorm) return true;
            const blob = [
                row.modulename,
                row.username,
                row.type,
                row.time,
                row.created_at,
                row.module_id,
                row.data,
            ]
                .filter((x) => x != null && String(x) !== "")
                .join(" ")
                .toLowerCase();
            return blob.includes(searchNorm);
        });
    }, [activityRows, searchNorm, dateFrom, dateTo, applyDateFilter]);

    const hasClientFilter = !!searchNorm || !!dateFrom || !!dateTo;

    useEffect(() => {
        if (subTab === "login") fetchLogin();
        else fetchActivity();
    }, [subTab, skip, fetchLogin, fetchActivity]);

    const total = subTab === "login" ? loginTotal : activityTotal;
    const loading = subTab === "login" ? loginLoading : activityLoading;
    const error = subTab === "login" ? loginError : activityError;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

    const goPrev = () => setSkip((s) => Math.max(0, s - PAGE_SIZE));
    const goNext = () => setSkip((s) => (s + PAGE_SIZE < total ? s + PAGE_SIZE : s));

    return (
        <div className="flex flex-col h-full min-h-[520px]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                    <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <i className="bi bi-journal-text text-primary-500"></i>
                        Audit &amp; monitoring
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Review sign-ins and detailed change history across modules.
                    </p>
                </div>
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <span className="mr-1">Settings</span> /{" "}
                    <span className="ml-1 font-semibold">Audit</span>
                </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex flex-wrap gap-2 mb-4">
                <button
                    type="button"
                    onClick={() => setSubTab("activity")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                        subTab === "activity"
                            ? "bg-primary-500 text-white border-primary-500 shadow-md"
                            : "bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-primary-400"
                    }`}
                >
                    <i className="bi bi-activity mr-2"></i>
                    Activity log
                </button>
                <button
                    type="button"
                    onClick={() => setSubTab("login")}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                        subTab === "login"
                            ? "bg-primary-500 text-white border-primary-500 shadow-md"
                            : "bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-primary-400"
                    }`}
                >
                    <i className="bi bi-box-arrow-in-right mr-2"></i>
                    Login log
                </button>
                <button
                    type="button"
                    onClick={() => (subTab === "login" ? fetchLogin() : fetchActivity())}
                    disabled={loading}
                    className="ml-auto px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                    <i className="bi bi-arrow-clockwise mr-1"></i>
                    Refresh
                </button>
            </div>

            {error && (
                <div className="mb-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200">
                    {error}
                </div>
            )}

            <div className="mb-3 flex flex-col gap-2">
                <div className="flex flex-col md:flex-row md:items-end md:flex-wrap gap-3">
                    <div className="relative flex-1 min-w-0 md:min-w-[200px]">
                        <i
                            className="bi bi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm"
                            aria-hidden
                        />
                        <input
                            type="search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={
                                subTab === "activity"
                                    ? "Search module, user, action, or change payload…"
                                    : "Search user, IP, or device…"
                            }
                            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-9 pr-10 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                            autoComplete="off"
                            spellCheck={false}
                        />
                        {searchQuery ? (
                            <button
                                type="button"
                                onClick={() => setSearchQuery("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200"
                                title="Clear search"
                                aria-label="Clear search"
                            >
                                <i className="bi bi-x-lg text-sm" />
                            </button>
                        ) : null}
                    </div>
                    <div className="flex flex-wrap items-end gap-3 shrink-0">
                        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium">From</span>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                            />
                        </label>
                        <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
                            <span className="font-medium">To</span>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
                            />
                        </label>
                        {(dateFrom || dateTo) && (
                            <button
                                type="button"
                                onClick={() => {
                                    setDateFrom("");
                                    setDateTo("");
                                }}
                                className="mb-0.5 px-2 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Clear dates
                            </button>
                        )}
                    </div>
                    {hasClientFilter ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400 md:ml-auto md:text-right md:shrink-0">
                            {subTab === "login"
                                ? `${filteredLoginRows.length} of ${loginRows.length} on this page`
                                : `${filteredActivityRows.length} of ${activityRows.length} on this page`}
                        </p>
                    ) : null}
                </div>
                {dateRangeInvalid && (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                        &quot;From&quot; must be on or before &quot;To&quot;. Date filtering is ignored until you fix the range.
                    </p>
                )}
            </div>

            <div className="relative rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gradient-to-b from-gray-50/80 to-white dark:from-gray-900/40 dark:to-gray-800 flex-1 flex flex-col min-h-0">
                {loading && (
                    <div className="absolute inset-0 z-10 bg-white/60 dark:bg-gray-900/50 flex items-center justify-center pointer-events-none">
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <span className="inline-block h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                            Loading…
                        </div>
                    </div>
                )}

                {subTab === "login" ? (
                    <div className="overflow-x-auto flex-1">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-100 dark:bg-gray-900/80 text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wide">
                                <tr>
                                    <th className="px-4 py-3 font-semibold">User</th>
                                    <th className="px-4 py-3 font-semibold">IP</th>
                                    <th className="px-4 py-3 font-semibold">Device / browser</th>
                                    <th className="px-4 py-3 font-semibold whitespace-nowrap">Signed in</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {!loading && loginRows.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                                            No login records for this page.
                                        </td>
                                    </tr>
                                )}
                                {!loading &&
                                    loginRows.length > 0 &&
                                    filteredLoginRows.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                                                No rows match your filters on this page.
                                            </td>
                                        </tr>
                                    )}
                                {filteredLoginRows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-primary-50/50 dark:hover:bg-gray-700/40 transition-colors"
                                    >
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                                            {row.loginname || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-mono text-xs">
                                            {row.ip?.trim() ? row.ip : "—"}
                                        </td>
                                        <td
                                            className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-md"
                                            title={row.info || ""}
                                        >
                                            {truncate(row.info || "", 80)}
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                            {formatTimestamp(row.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="overflow-x-auto flex-1">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-100 dark:bg-gray-900/80 text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wide">
                                <tr>
                                    <th className="px-3 py-3 font-semibold">Module</th>
                                    <th className="px-3 py-3 font-semibold">User</th>
                                    <th className="px-3 py-3 font-semibold">Action</th>
                                    <th className="px-3 py-3 font-semibold whitespace-nowrap">Time</th>
                                    <th className="px-3 py-3 font-semibold text-right whitespace-nowrap">Details</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {!loading && activityRows.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                                            No activity records for this page.
                                        </td>
                                    </tr>
                                )}
                                {!loading &&
                                    activityRows.length > 0 &&
                                    filteredActivityRows.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                                                No rows match your filters on this page.
                                            </td>
                                        </tr>
                                    )}
                                {filteredActivityRows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-primary-50/50 dark:hover:bg-gray-700/40 transition-colors"
                                    >
                                        <td className="px-3 py-2 font-mono text-xs text-gray-800 dark:text-gray-200 max-w-[200px] break-all">
                                            {row.modulename || "—"}
                                        </td>
                                        <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                            {row.username || "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span
                                                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${typeBadgeClass(
                                                    row.type
                                                )}`}
                                            >
                                                {row.type || "—"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                                            {formatTimestamp(row.time || row.created_at)}
                                        </td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => setDetailRow(row)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors"
                                            >
                                                <i className="bi bi-list-ul"></i>
                                                View details
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Showing{" "}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                        {total === 0 ? 0 : skip + 1}–{Math.min(skip + PAGE_SIZE, total)}
                    </span>{" "}
                    of <span className="font-medium">{total}</span>
                    {searchNorm || applyDateFilter ? (
                        <span className="text-gray-400 dark:text-gray-500">
                            {" "}
                            · filters apply to the current page only
                        </span>
                    ) : null}
                </p>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={goPrev}
                        disabled={skip === 0 || loading}
                        className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-40"
                    >
                        Previous
                    </button>
                    <span className="text-xs text-gray-600 dark:text-gray-400 px-2">
                        Page {currentPage} / {pageCount}
                    </span>
                    <button
                        type="button"
                        onClick={goNext}
                        disabled={skip + PAGE_SIZE >= total || loading}
                        className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-40"
                    >
                        Next
                    </button>
                </div>
            </div>

            {detailRow && (
                <ActivityDetailModal row={detailRow} onClose={() => setDetailRow(null)} />
            )}
        </div>
    );
};

const ActivityDetailModal: React.FC<{
    row: ActivityAuditRow;
    onClose: () => void;
}> = ({ row, onClose }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const { fields, submoduleBlocks, parseError } = parseAuditDataPayload(row.data ?? "");
    const rawPretty = useMemo(() => {
        const raw = row.data ?? "";
        try {
            return JSON.stringify(JSON.parse(raw), null, 2);
        } catch {
            return raw || "—";
        }
    }, [row.data]);

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
            <button
                type="button"
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                aria-label="Close details"
                onClick={onClose}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="audit-detail-title"
                className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
            >
                <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
                    <div>
                        <h3 id="audit-detail-title" className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            Change details
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono break-all">
                            {row.modulename || "—"}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                <span className="text-gray-500">User:</span> {row.username || "—"}
                            </span>
                            <span
                                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${typeBadgeClass(
                                    row.type
                                )}`}
                            >
                                {row.type || "—"}
                            </span>
                            <span className="text-xs text-gray-500">
                                {formatTimestamp(row.time || row.created_at)}
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200"
                        aria-label="Close"
                    >
                        <i className="bi bi-x-lg text-lg"></i>
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-4 space-y-6 custom-scrollbar">
                    {!parseError && fields.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
                                <i className="bi bi-arrow-left-right text-primary-500"></i>
                                Fields added or changed
                            </h4>
                            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-900/80 text-left text-xs uppercase text-gray-600 dark:text-gray-400">
                                        <tr>
                                            <th className="px-3 py-2 font-semibold">Field</th>
                                            <th className="px-3 py-2 font-semibold">Previous</th>
                                            <th className="px-3 py-2 font-semibold">New</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {fields.map((f, idx) => (
                                            <tr key={`${f.fieldName}-${idx}`} className="bg-white dark:bg-gray-800/80">
                                                <td className="px-3 py-2 align-top text-gray-900 dark:text-gray-100">
                                                    <span className="font-medium">{f.displayLabel}</span>
                                                    {f.fieldName && f.fieldName !== f.displayLabel && (
                                                        <span className="block text-xs font-mono text-gray-500 mt-0.5">
                                                            {f.fieldName}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 align-top text-gray-600 dark:text-gray-400 break-words max-w-[200px]">
                                                    {f.oldVal}
                                                </td>
                                                <td className="px-3 py-2 align-top text-gray-900 dark:text-gray-100 break-words max-w-[200px]">
                                                    {f.newVal}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {submoduleBlocks.length > 0 && (
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
                                <i className="bi bi-diagram-3 text-primary-500"></i>
                                Submodule / lines
                            </h4>
                            {submoduleBlocks.map((block, i) => (
                                <pre
                                    key={i}
                                    className="text-xs font-mono text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-600 rounded-lg p-3 mb-2 max-h-56 overflow-auto custom-scrollbar whitespace-pre-wrap break-words"
                                >
                                    {block}
                                </pre>
                            ))}
                        </div>
                    )}

                    {(parseError ||
                        (fields.length === 0 && submoduleBlocks.length === 0)) && (
                        <div>
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                Full payload
                            </h4>
                            <pre className="text-xs font-mono text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-600 rounded-lg p-3 max-h-72 overflow-auto custom-scrollbar whitespace-pre-wrap break-words">
                                {rawPretty}
                            </pre>
                        </div>
                    )}

                    {!parseError && fields.length > 0 && (
                        <details className="group">
                            <summary className="cursor-pointer text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline list-none flex items-center gap-2">
                                <i className="bi bi-code-slash"></i>
                                Raw JSON
                                <i className="bi bi-chevron-down text-xs group-open:rotate-180 transition-transform"></i>
                            </summary>
                            <pre className="mt-2 text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-600 rounded-lg p-3 max-h-48 overflow-auto custom-scrollbar whitespace-pre-wrap break-words">
                                {rawPretty}
                            </pre>
                        </details>
                    )}
                </div>

                <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-end shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-500 text-white hover:bg-primary-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
